import { Component, OnInit, Input, Output, EventEmitter, ViewChild, OnDestroy, ChangeDetectorRef  } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Subscription } from 'rxjs/Subscription';

import { FeedManager } from '../shared/feed-manager.service';
import { FeedGridService } from '../shared/feed-grid.service';

import { FeedMetadataColumn } from '../shared/models/feed-metadata-column.model';
import { FeedColumnLookup } from '../shared/models/feed-column-lookup.model';
import { SearchPagingResult } from '../shared/models/search-paging-result.model';
import { PagerLocation } from './pager-location.model';
import { FeedGridHeaderComponent } from '../feed-grid-header/feed-grid-header.component';
import { DateFilterModel } from '../../../shared/datepicker-list/date-filter.model';
import { SearchSpecification } from '../shared/models/search-specification.model';
import { FeedMetadataDetail } from '../shared/models/feed-metadata-detail.model';
import { FeedSearchResult } from '../shared/models/feed-search-result.model';
import { TimeDurationUnit } from './../../../shared/timepicker/time-picker-interval';
import { FeedService } from '../shared/feed.service';
import { EventLoggerService } from '../../../shared/event-logger.service';
import { ErrorManagerService } from '../../../shared/error-handling/error-manager.service';

@Component({
  selector: 'feed-grid',
  templateUrl: './feed-grid.component.html',
  styleUrls: ['./feed-grid.component.css']
})
export class FeedGridComponent implements OnInit, OnDestroy {
    @Input() allowExport = true;
    @Input() pagerLocation: PagerLocation = PagerLocation.Both;
    @Input() height = -1;
    @Input() heightPercent = .7;
    @Input() enableSelections = false;

    @Output() rowSelectionChanged = new EventEmitter<any[]>();
    @Output() pubFeedGridError = new EventEmitter<any>();

    @ViewChild(FeedGridHeaderComponent) gridHeader: FeedGridHeaderComponent;

    feedColumns: FeedMetadataColumn[];
    feedColumnLookups: FeedColumnLookup[];
    feedRows: any[];
    searchPagingResult: SearchPagingResult;
    gridTopFilters: DateFilterModel[] = new Array<DateFilterModel>();
    searchSpecification: SearchSpecification;
    outboundIsArchive: boolean;
    feedName: string;
    metadataDetail: FeedMetadataDetail;
    activeDateRangeFilter: DateFilterModel;
    initialized = false;
    searchResult: FeedSearchResult;
    activeRowHover: any[] = new Array<any>();
    selectedRows: any[] = new Array<any>();
    identityField: string;
    displayField: string;
    filters: any[] = [];
    activeSortField: string;
    activeSortOrderIsDesc: boolean;
    disableReset: boolean;
    disabledResetFeed: string[] = [];
    metadataSubscription: Subscription;
    searchSubscription: Subscription;
    selectionSubscription: Subscription;
    lookupSubscription: Subscription;
    routeSubscription: Subscription;
    feedTimeInterval = '';
    error: any;
    errorKey = 'feed-grid';

    constructor(private feedManager: FeedManager,
        private route: ActivatedRoute,
        private gridService: FeedGridService,
        private changeDetect: ChangeDetectorRef,
        private feedService: FeedService,
        private eventLogger: EventLoggerService,
        private errorManager: ErrorManagerService) {

        this.selectionSubscription = this.gridService.selectedSelectListRowsObservable.subscribe(
            rows => {
                // selections removed need to update the grids selections
                const temp = this.selectedRows.slice(0);
                temp.forEach(row => {
                    const id = row[this.identityField];
                    const found = rows.find(x => x[this.identityField] === id);
                    if (!found) {
                        const rowToDelete = this.selectedRows.find(x => x[this.identityField] === id);
                        const index = this.selectedRows.indexOf(rowToDelete);
                        this.selectedRows.splice(index, 1);
                    }
                });
                // make sure subscribes now about updated row selections
                this.gridService.setSelectedGridRows(this.selectedRows);
            }
        );
    }

    ngOnInit() {
        this.clearError();
        this.initalize();
    }

    ngOnDestroy() {
        this.changeDetect.detach();

        if (this.selectionSubscription) {
          this.selectionSubscription.unsubscribe();
        }

        if (this.metadataSubscription) {
          this.metadataSubscription.unsubscribe();
        }

        if (this.lookupSubscription) {
          this.lookupSubscription.unsubscribe();
        }

        if (this.searchSubscription) {
          this.searchSubscription.unsubscribe();
        }

        if (this.routeSubscription) {
          this.routeSubscription.unsubscribe();
        }
    }

    initalize(): void {
        this.routeSubscription = this.route.params.subscribe((params: Params) => { this.feedName = params['feed']; });
        this.searchSpecification = new SearchSpecification();
        const metadataObservable = this.feedManager.getFeedMetadataDetail(this.feedName);

        // Set up error observable
        if (this.feedManager.ErrorObservable) {
            this.feedManager.ErrorObservable.subscribe(
                error => {
                    this.error = error;
                    this.initialized = true;
                    this.pubFeedGridError.emit(error);
                    this.handleError(error);
                }
            );
        }

        const searchObservable = this.feedManager.SearchObservable;
        if (searchObservable) {
            this.searchSubscription = searchObservable.subscribe(
                this.bindSearchResults.bind(this),
                error => {
                    this.error = error;
                    this.handleError(error);
                }
            );
        }

        this.metadataSubscription = metadataObservable.subscribe(
            (detail: FeedMetadataDetail) => {
                this.metadataDetail = detail;
                this.gridService.setMetadataDetail(detail);
                this.disableReset = this.disabledResetFeed.indexOf(detail.name) != -1;

                if (this.metadataDetail.feedSelectorConfiguration) {
                    this.identityField = this.metadataDetail.feedSelectorConfiguration.sourceIdentityField;
                    this.displayField = this.metadataDetail.feedSelectorConfiguration.sourceDisplayField;
                    const selectorDetail = this.gridService.getDetailsFromParent();

                    if (selectorDetail && selectorDetail.selectedRows && selectorDetail.selectedRows.length > 0) {
                            this.selectedRows = selectorDetail.selectedRows.slice(0);
                            this.gridService.setSelectedGridRows(selectorDetail.selectedRows.slice(0));
                            this.gridService.setSelectListRows(selectorDetail.selectedRows.slice(0));
                    }
                }

                this.gridService.setTopFilterBox(this.gridTopFilters.length > 0);

                const lookupObservable = this.feedManager.getFeedLookupData(this.feedName, detail);
                this.lookupSubscription = lookupObservable.subscribe(
                    (lookupData) => {
                        this.feedColumnLookups = lookupData;
                    }
                );

                // if there is a default search json available, then don't bother setting start-row and rowcount onInit.
                if (!detail.defaultSearchJson) {
                    this.searchSpecification.startRow = 1;
                    this.searchSpecification.rowCount = 15;
                }
                this.outboundIsArchive = false;
                this.search();

                // set time picker time interval for special feeds
                this.setFeedTimeInterval(detail.timeDurationUnit, detail.timeDurationIncrement);
            }
        );
    }

    private setFeedTimeInterval(timeDurationUnit: number, timeDurationIncrement: number) {
        const zeros = '00';
        this.feedTimeInterval = `${timeDurationIncrement}:${zeros}`;

        if (timeDurationUnit === TimeDurationUnit.Minutes) {
            this.feedTimeInterval = `${zeros}:${timeDurationIncrement}`;
        } else if (timeDurationUnit === TimeDurationUnit.Seconds) {
            this.feedTimeInterval = `${zeros}:${zeros}:${timeDurationIncrement}`;
        }
    }

    search(): void {
        this.clearError();
        if (this.error) {
            this.error = undefined;
        }
        this.initialized = false;
        
        this.feedManager.search(this.feedName, this.searchSpecification, this.outboundIsArchive);
    }

    bindSearchResults(result: FeedSearchResult): void {
        this.searchResult = result;
        this.feedRows = this.searchResult.items;
        const fields = this.searchResult.searchSpecification.fields;

        const columns = [];
        this.metadataDetail.columns.forEach(metadataColumn => {
            if (fields.indexOf(metadataColumn.fieldName) > -1 && metadataColumn.isVisible) {
                metadataColumn.fieldName = metadataColumn.fieldName;
                columns.push(metadataColumn);
            }
        });
        this.feedColumns = columns;

        this.searchPagingResult = this.feedManager.getSearchPagingResult(this.searchResult);
        this.searchSpecification = this.searchResult.searchSpecification;
        this.filters = this.feedManager.buildFiltersFromSearchSpecification(this.searchResult.searchSpecification.filters, this.metadataDetail);
        this.activeSortField = this.searchSpecification.sort;
        this.activeSortOrderIsDesc = this.searchSpecification.order && this.searchSpecification.order.toLowerCase() === 'desc';
        this.setupGridTopFilters();
        this.initialized = true;
        this.changeDetect.detectChanges();
    }

    onSortChanged(event: any): void {
        this.searchSpecification.startRow = 1;
        this.searchSpecification.sort = event.sortField;
        this.searchSpecification.order = event.sortDesc ? 'DESC' : 'ASC';
        this.search();
    }

    onFilterChanged(data: any) {
        const filters = data;
        if (this.activeDateRangeFilter) {
            filters[this.activeDateRangeFilter.fieldName] = this.activeDateRangeFilter.dateRange;
        }
        this.searchSpecification.filters = this.feedManager.buildSearchSpecificationFilters(filters, this.metadataDetail);
        // only set if specified (reset nulls these out and if we pass something, we must pass everything)
        if (this.searchSpecification.startRow) {
          this.searchSpecification.startRow = 1;
        }

        this.search();
    }

    onPageSizeChanged(value: number) {
        this.searchSpecification.startRow = 1;
        this.searchSpecification.rowCount = value;
        this.search();
    }

    onPageChanged(startRow: number) {
        this.searchSpecification.startRow = startRow;
        this.search();
    }

    onScroll(event: any) {
        if (this.height === -1) {
            this.gridHeader.closeFilters();
        }
    }

    reset(): void {
        // reset the properties we control
        this.searchSpecification = new SearchSpecification();
        this.activeDateRangeFilter = null;
        this.outboundIsArchive = false;
        // delegate to TH's controls
        this.gridHeader.reset();
    }

    onDateRangeFilter(value: DateFilterModel): void {
        this.activeDateRangeFilter = value;
        const filters = [];
        filters[this.activeDateRangeFilter.fieldName] = this.activeDateRangeFilter.dateRange;
        this.outboundIsArchive = false;
        if (this.metadataDetail.enableArchiving) {
            let cutOffDate = new Date();
            cutOffDate.setHours(0, 0, 0, 0);
            cutOffDate.setDate(cutOffDate.getDate() - this.metadataDetail.archiveCutoffDays);

            if ((new Date(value.startDate)) < cutOffDate || (new Date(value.endDate)) < cutOffDate)
                this.outboundIsArchive = true;
        }    
        this.onFilterChanged(filters);
    }

    onDateRangeReset(): void {
        this.reset();
    }

    getGridHeight(): any {
        if (this.height > -1) {
            return this.height;
        } else {
          return window.innerHeight * this.heightPercent;
        }
    }

    onMouseOver(index: number): void {
        if (this.enableSelections) {
            this.activeRowHover[index] = true;
        }
    }

    onMouseLeave(index: number): void {
        if (this.enableSelections) {
            this.activeRowHover[index] = false;
        }
    }

    toggleRowSelect(row: any, event: any): void {
        if (this.enableSelections && this.identityField) {
            const id = row[this.identityField];
            const selected = this.selectedRows.find(x => x[this.identityField] === id);
            if (!selected) {
                // Not there? add it
                this.selectedRows.push(row);
            } else {
                // there? remove it.
                const index = this.selectedRows.indexOf(selected);
                this.selectedRows.splice(index, 1);
            }
            // update the grid service so subscribers are notified.
            this.gridService.setSelectedGridRows(this.selectedRows);
          }
    }

    rowIsSelected(row: any): boolean {
        let result = false;
        if (this.enableSelections && this.identityField) {
            const id = row[this.identityField];
            result = this.selectedRows.find(x => x[this.identityField] === id) !== undefined;
        }
        return result;
    }

    setupGridTopFilters() {
        this.metadataDetail.columns.forEach((column: FeedMetadataColumn) => {
            if (column.enableFiltering
                && column.filterUiLocation === 'GridTop' // TODO: move this out to an enum.
                && (column.dataType.toLowerCase() === 'date'
                    || column.dataType.toLowerCase() === 'datetime')) {

                this.gridTopFilters = [];

                const filter = new DateFilterModel();
                filter.dataType = column.dataType;
                filter.fieldName = column.fieldName;
                filter.displayName = column.displayName;

                if (this.filters[column.fieldName]) {
                    // determine filters start/end dates from value return
                    // from server
                    const dateRanges = this.feedManager.parseDateFilter(this.filters[column.fieldName], column);
                    filter.startDate = dateRanges.startDate;
                    filter.startTime = dateRanges.startTime;
                    filter.endDate = dateRanges.endDate;
                    filter.endTime = dateRanges.endTime;
                }
                this.gridTopFilters.push(filter);
            }
        });

    }

    setErrorForDownloadLink() {
        if (this.error) { this.error = undefined; }
        this.clearError();
    }

    getDownloadLinkError(error: any) {
        this.error = error;
    }

    clearError() {
        this.errorManager.removeDataMinerUiError(this.errorManager.errorExploreDataSetKey, this.errorKey);
    }

    handleError(error: any) {
        const FeedExploreDataSetNoResponse = 'An unexpected error occurred. Please try again.';
        this.errorManager.saveDataMinerUiError(this.errorManager.errorExploreDataSetKey, this.errorKey, error, FeedExploreDataSetNoResponse);
        this.eventLogger.logError(new Error(this.errorManager.dataMinerUiError.message), this.errorManager.dataMinerUiError.message, {});
    }
}



// WEBPACK FOOTER //
// ./src/app/main/feeds/feed-grid/feed-grid.component.ts